import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      const sectionId = this.getAttribute('section-id') || '';
      const key = sectionId
        ? `product-grid-view-${viewport}:${sectionId}`
        : `product-grid-view-${viewport}`;
      sessionStorage.setItem(key, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isDesktop = event.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';
    const sectionId = this.getAttribute('section-id') || '';
    const grid = this.refs.grid;
    if (!grid) return;

    const defaultViewAttr = isDesktop ? 'data-default-grid-view-desktop' : 'data-default-grid-view-mobile';
    const merchantDefault = grid.getAttribute(defaultViewAttr) || 'mobile-multi';
    const forceDefault = grid.getAttribute('data-force-default-grid-view') === 'true';

    let activeLayout = merchantDefault;
    if (!forceDefault && sectionId) {
      const stored = sessionStorage.getItem(`product-grid-view-${viewport}:${sectionId}`);
      if (stored) activeLayout = stored;
    }

    this.#setLayout(activeLayout);

    const radioName = isDesktop ? 'grid' : 'grid-mobile';
    const radios = this.querySelectorAll(`input[type="radio"][name="${radioName}"]`);
    for (const radio of radios) {
      if (radio instanceof HTMLInputElement) {
        radio.checked = radio.value === activeLayout;
      }
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
